import {
  Category,
  Dashboard,
  DashboardCumulativeProfitability,
  DashboardProfitability,
  DashboardWhitespace,
  ErrorType,
  ProfitabilityChangeGroup,
  ProfitabilityCumulativeChangeGroup,
  RevenueChangeGroup,
  TokenValidationFailure,
  WhiteSpaceLegend,
} from 'enums';
import { ThemeColors } from 'providers/Theme';

interface CategoryConfigItem {
  value: number;
  label: string;
  color: ThemeColors;
}

interface CategoryConfig {
  values: CategoryConfigItem[];
  default: {
    label: string;
    color: ThemeColors;
  };
}

const categories: CategoryConfig = {
  values: [
    {
      value: 1,
      label: 'Platinum',
      color: 'platinum',
    },
    {
      value: 2,
      label: 'Gold',
      color: 'gold',
    },
    {
      value: 3,
      label: 'Silver',
      color: 'silver',
    },
    {
      value: 4,
      label: 'Bronze',
      color: 'bronze',
    },
  ],
  default: {
    label: 'Select All',
    color: 'black',
  },
};

const language = {
  auth: {
    login: {
      error_message:
        'The combination of email and password is incorrect. Please try again, or reset your password to access your account.',
      server_error_message:
        'Your submission experienced a network error. Please try again',
    },
    microsoft_change_password: {
      paragraph_1:
        'You currently use your Microsoft 365 account to sign into BeeCastle. Passwords can not be reset within BeeCastle for accounts using Microsoft 365 sign-in.',
      paragraph_2:
        'Please reset your Microsoft 365 account password within Microsoft 365 or by contacting your Microsoft administrator.',
    },
    microsoft_login: {
      errors: {
        no_account:
          'Could not find a BeeCastle account using that Microsoft account',
      },
    },
    microsoft_signup: {
      errors: {
        general_error: 'Microsoft sign in failed.  Please try again.',
        is_registered: `This user is already registered.  Proceed to app.beecastle.com to sign in.`,
        invite_email_mismatch:
          'Your 365 account must match the email address the BeeCastle invite was sent to. Try again with your invited email address.',
      },
    },
  },
  components: {
    password_strength: {
      values: ['Weak', 'Fair', 'Good', 'Strong', 'Perfect'],
    },
  },
  dashboards: {
    analytics: {
      tooltips: {
        meetings:
          'Total number of unique meetings involving selected users during the period',
        meetingsFirst:
          'Total number of meetings that were the first meetings with the attending company. This can serve as a indicator for prospecting and new relationship development',
        calls:
          'Total number of unique calls involving selected users during the period',
        duration:
          'Total time of unique meetings and calls involving selected users during the period',
        emails:
          'Total number of outbound and inbound emails involving selected users during the period',
        metcalled:
          'Total number of companies that were involved in meetings with selected users during the period',
      },
    },
    whitespace: {
      [DashboardWhitespace.WHITESPACE_AGREEMENT_TYPE]: {
        legend: {
          item: {
            children: {
              [WhiteSpaceLegend.ACTIVE]: 'Active',
              [WhiteSpaceLegend.ANNOTATED]: 'Annotated',
              [WhiteSpaceLegend.OPEN_OPPORTUNITY]: 'Open Opportunity',
              [WhiteSpaceLegend.AGREEMENT_TYPE_ACTIVE]: 'Active',
              [WhiteSpaceLegend.AGREEMENT_TYPE_PROSPECT]: 'Possible Prospect',
              [WhiteSpaceLegend.AGREEMENT_TYPE_HEADER]: 'Agreement Type',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_ACTIVE]:
                'Active Subcategory',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_HEADER]:
                'Product Subcategory',
              [WhiteSpaceLegend.INVOICED_PRODUCT_ACTIVE]: 'Active',
              [WhiteSpaceLegend.INVOICED_PRODUCT_HEADER]: 'Product Description',
            },
            tooltipText: {
              [WhiteSpaceLegend.ACTIVE]:
                'Indicates a company has an active agreement of that type.',
              [WhiteSpaceLegend.ANNOTATED]:
                'Indicates the whitespace opportunity has been annotated by your team. Use this to track your effort as you work through the whitespace opportunities.',
              [WhiteSpaceLegend.OPEN_OPPORTUNITY]:
                'Indicates an opportunity has been created by your team for this agreement type.  Only opportunities created through BeeCastle will be annotated here, and will not be displayed for active agreements.',
              [WhiteSpaceLegend.AGREEMENT_TYPE_ACTIVE]:
                'Indicates a company has an active agreement for that type.',
              [WhiteSpaceLegend.AGREEMENT_TYPE_PROSPECT]: `Indicates a possible prospecting opportunity. It has no active agreements of this type. 
                Prospects are suggested when other companies with similar agreements have active agreements of this type.  
                The darker the green shading, the greater the number of similar companies.`,
              [WhiteSpaceLegend.AGREEMENT_TYPE_HEADER]:
                'Values represent the revenue a company has generated from agreements of these types.',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_ACTIVE]:
                'Indicates a company has been invoiced for some product belonging to this subcategory.',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_HEADER]:
                'Values represent invoiced product revenue across all agreements over the period. These can include products charged to an addition. Columns represent the grouping of products into their sub-category.',
              [WhiteSpaceLegend.INVOICED_PRODUCT_ACTIVE]:
                'Indicates a company has an active product addition of that description.',
              [WhiteSpaceLegend.INVOICED_PRODUCT_HEADER]:
                'Values represent the invoiced revenue across all agreements for a given product. This can include products charged to an addition.',
            },
          },
        },
      },
      [DashboardWhitespace.WHITESPACE_PRODUCT_ADDITIONS]: {
        legend: {
          item: {
            children: {
              [WhiteSpaceLegend.ACTIVE]: 'Active',
              [WhiteSpaceLegend.ANNOTATED]: 'Annotated',
              [WhiteSpaceLegend.OPEN_OPPORTUNITY]: 'Open Opportunity',
              [WhiteSpaceLegend.AGREEMENT_TYPE_ACTIVE]: 'Active Agreeement',
              [WhiteSpaceLegend.AGREEMENT_TYPE_PROSPECT]: 'Possible Prospect',
              [WhiteSpaceLegend.AGREEMENT_TYPE_HEADER]: 'Agreement Type',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_ACTIVE]:
                'Active Subcategory',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_HEADER]:
                'Product Subcategory',
              [WhiteSpaceLegend.INVOICED_PRODUCT_ACTIVE]: 'Active',
              [WhiteSpaceLegend.INVOICED_PRODUCT_HEADER]: 'Product Description',
            },
            tooltipText: {
              [WhiteSpaceLegend.ACTIVE]:
                'Indicates a company has an active agreement addition of that description.',
              [WhiteSpaceLegend.ANNOTATED]:
                'Indicates the whitespace opportunity has been annotated by your team. Use this to track your effort as you work through the whitespace opportunities.',
              [WhiteSpaceLegend.OPEN_OPPORTUNITY]:
                'Indicates an opportunity has been created by your team for this subcategory or product.  Only opportunities created through BeeCastle will be annotated here, and will not be displayed for active additions, or product subcategories with active additions.',
              [WhiteSpaceLegend.AGREEMENT_TYPE_ACTIVE]:
                'Indicates a company has an active agreement for that type.',
              [WhiteSpaceLegend.AGREEMENT_TYPE_PROSPECT]: `Indicates a possible prospecting opportunity. It has no active additions or products of this type. 
                Prospects are suggested when other companies with similar additions or invoiced products have active additions or invoiced products of this type.  
                The darker the green shading, the greater the number of similar companies.  
                Prospect cells may provide an estimate of the value of this opportunity "(est)".
                Estimates based on product and peer revenue plus total revenue comparisons.`,
              [WhiteSpaceLegend.AGREEMENT_TYPE_HEADER]:
                'Values represent the revenue a company has generated from agreements of these types.',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_ACTIVE]:
                'Indicates a company has been invoiced for some product belonging to this subcategory.',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_HEADER]:
                'Values represent invoiced product revenue across all agreements over the period. These can include products charged to an addition. Columns represent the grouping of products into their sub-category.',
              [WhiteSpaceLegend.INVOICED_PRODUCT_ACTIVE]:
                'Indicates a company has an active product addition of that description.',
              [WhiteSpaceLegend.INVOICED_PRODUCT_HEADER]:
                'Values represent the invoiced revenue across all agreements for a given product. This can include products charged to an addition.',
            },
          },
        },
      },
      [DashboardWhitespace.WHITESPACE_AGREEMENT_ADDITION_MAPPING]: {
        legend: {
          item: {
            children: {
              [WhiteSpaceLegend.ACTIVE]: 'Active',
              [WhiteSpaceLegend.ANNOTATED]: 'Annotated',
              [WhiteSpaceLegend.OPEN_OPPORTUNITY]: 'Open Opportunity',
              [WhiteSpaceLegend.AGREEMENT_TYPE_ACTIVE]: 'Active Agreement',
              [WhiteSpaceLegend.AGREEMENT_TYPE_PROSPECT]: 'Possible Prospect',
              [WhiteSpaceLegend.AGREEMENT_TYPE_HEADER]: 'Agreement Type',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_ACTIVE]:
                'Active Subcategory',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_HEADER]:
                'Product Subcategory',
              [WhiteSpaceLegend.INVOICED_PRODUCT_ACTIVE]: 'Active',
              [WhiteSpaceLegend.INVOICED_PRODUCT_HEADER]: 'Product Description',
            },
            tooltipText: {
              [WhiteSpaceLegend.ACTIVE]:
                'Indicates a company has an active agreement of that type or active agreement addition of that description.',
              [WhiteSpaceLegend.ANNOTATED]:
                'Indicates the whitespace opportunity has been annotated by your team. Use this to track your effort as you work through the whitespace opportunities.',
              [WhiteSpaceLegend.OPEN_OPPORTUNITY]:
                'Indicates an opportunity has been created by your team for this agreement type, subcategory or product addition.  Only opportunities created through BeeCastle will be annotated here, and will not be displayed here for active agreements or products.',
              [WhiteSpaceLegend.AGREEMENT_TYPE_ACTIVE]:
                'Indicates a company has an active agreement for that type.',
              [WhiteSpaceLegend.AGREEMENT_TYPE_PROSPECT]: `Indicates a possible prospecting opportunity. If the cell is situated in the "Total Agreement Type Revenue" column, 
                then it indicates the company has no active agreements of this type. 
                Prospects are suggested for an Agreement Type when other companies with similar agreements have active agreements of this type.  
                If the cell is situated in an individual Product Description column, then it indicates the company has no active additions or products of this type. 
                Prospects are suggested for an individual Product when other companies with similar additions or invoiced products have active additions or invoiced products of this type.
                The darker the green shading, the greater the number of similar companies.
                Prospect cells may provide an estimate of the value of this opportunity "(est)".
                Estimates are based on product and peer revenue plus total revenue comparisons.`,
              [WhiteSpaceLegend.AGREEMENT_TYPE_HEADER]:
                'Values represent the revenue a company has generated from agreements of these types.',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_ACTIVE]:
                'Indicates a company has been invoiced for some product belonging to this subcategory.',
              [WhiteSpaceLegend.PRODUCT_SUBCATEGORY_HEADER]:
                'Values represent invoiced product revenue across all agreements over the period. These can include products charged to an addition. Columns represent the grouping of products into their sub-category.',
              [WhiteSpaceLegend.INVOICED_PRODUCT_ACTIVE]:
                'Indicates a company has an active product addition of that description.',
              [WhiteSpaceLegend.INVOICED_PRODUCT_HEADER]:
                'Values represent the invoiced revenue across all agreements for a given product. This can include products charged to an addition.',
            },
          },
        },
      },
    },
    profitability: {
      [DashboardProfitability.PROFITABILITY_CUSTOMER]: {
        legend: {
          item: {
            children: {
              [ProfitabilityChangeGroup.PROFIT]: 'Profit',
              [ProfitabilityChangeGroup.LOSS]: 'Loss',
              [ProfitabilityChangeGroup.COST]: 'Cost',
              [ProfitabilityChangeGroup.TOTAL_REVENUE]: 'Total Revenue',
            },
            tooltipText: {
              [ProfitabilityChangeGroup.PROFIT]:
                'Highlights a customer that generated a profit for the period.',
              [ProfitabilityChangeGroup.LOSS]:
                'Highlights a customer that generated a loss for the period.',
              [ProfitabilityChangeGroup.COST]: '',
              [ProfitabilityChangeGroup.TOTAL_REVENUE]: '',
            },
          },
        },
      },
      [DashboardProfitability.PROFITABILITY_COMPANY_DETAIL]: {
        legend: {
          item: {
            children: {
              [ProfitabilityChangeGroup.PROFIT]: 'Profit',
              [ProfitabilityChangeGroup.LOSS]: 'Loss',
              [ProfitabilityChangeGroup.COST]: 'Costs',
              [ProfitabilityChangeGroup.TOTAL_REVENUE]: 'Total Revenue',
            },
            tooltipText: {
              [ProfitabilityChangeGroup.PROFIT]:
                'The profit generated for the company over the period.',
              [ProfitabilityChangeGroup.LOSS]: '',
              [ProfitabilityChangeGroup.COST]:
                'Costs incurred for the company over the period',
              [ProfitabilityChangeGroup.TOTAL_REVENUE]:
                'Total revenue generated for the company over the period',
            },
          },
        },
      },
      [DashboardProfitability.PROFITABILITY_ACCOUNT]: {
        legend: {
          item: {
            children: {
              [ProfitabilityChangeGroup.PROFIT]: 'Profit',
              [ProfitabilityChangeGroup.LOSS]: 'Loss',
              [ProfitabilityChangeGroup.COST]: 'Costs',
              [ProfitabilityChangeGroup.TOTAL_REVENUE]: 'Total Revenue',
            },
            tooltipText: {
              [ProfitabilityChangeGroup.PROFIT]:
                'The profit generated from all of your customers over the period.',
              [ProfitabilityChangeGroup.LOSS]: '',
              [ProfitabilityChangeGroup.COST]:
                'Costs incurred from all of your customers over the period',
              [ProfitabilityChangeGroup.TOTAL_REVENUE]:
                'Total revenue generated from all of your customers over the period',
            },
          },
        },
      },
      [DashboardCumulativeProfitability.PROFITABILITY_CUMULATIVE_COMPANY_DETAIL]:
        {
          legend: {
            item: {
              children: {
                [ProfitabilityCumulativeChangeGroup.CUMULATIVE_PROFIT]:
                  'Cumulative Profit',
                [ProfitabilityCumulativeChangeGroup.POSITIVE_PROFIT]: 'Profit',
                [ProfitabilityCumulativeChangeGroup.NEGATIVE_PROFIT]:
                  'Losses Incurred',
                [ProfitabilityCumulativeChangeGroup.RETAINED_PROFIT]:
                  'Prior Earnings',
              },
              tooltipText: {
                [ProfitabilityCumulativeChangeGroup.CUMULATIVE_PROFIT]:
                  'The total profit accumulated up to the current period.',
                [ProfitabilityCumulativeChangeGroup.POSITIVE_PROFIT]:
                  'The amount by which gross revenue exceeded costs for the period.',
                [ProfitabilityCumulativeChangeGroup.NEGATIVE_PROFIT]:
                  'The amount by which costs exceeded gross revenue for the period.',
                [ProfitabilityCumulativeChangeGroup.RETAINED_PROFIT]:
                  'The sum of all profits and losses from all prior periods',
              },
            },
          },
        },
    },
    profitTrack: {
      tiering: {
        legend: {
          item: {
            children: {
              [Category.PLATINUM]: 'Platinum',
              [Category.GOLD]: 'Gold',
              [Category.SILVER]: 'Silver',
              [Category.BRONZE]: 'Bronze',
            },
            tooltipText: {
              [Category.PLATINUM]: '',
              [Category.GOLD]: '',
              [Category.SILVER]: '',
              [Category.BRONZE]: '',
            },
          },
        },
      },
      [Dashboard.AGREEMENT_REVENUE_CHANGE]: {
        legend: {
          item: {
            children: {
              [RevenueChangeGroup.SAME]: 'Same',
              [RevenueChangeGroup.CHURNED]: 'Churned',
              [RevenueChangeGroup.DECREASED]: 'Decreased Revenue',
              [RevenueChangeGroup.EXISTING]: 'Existing Revenue',
              [RevenueChangeGroup.INCREASED]: 'Increased Revenue',
              [RevenueChangeGroup.NEW]: 'New Revenue',
              [RevenueChangeGroup.NET]: 'Net Change',
              [RevenueChangeGroup.TOTAL]: 'Total Revenue',
            },
            tooltipText: {
              [RevenueChangeGroup.SAME]: '',
              [RevenueChangeGroup.CHURNED]: '',
              [RevenueChangeGroup.DECREASED]: `Net decrease in revenue vs prior period for customers who decreased agreement spend.`,
              [RevenueChangeGroup.EXISTING]: `Net revenue that hasn't changed from prior period.`,
              [RevenueChangeGroup.INCREASED]: `Net increase in revenune vs prior period for customers who increased agreement spend.`,
              [RevenueChangeGroup.NEW]:
                'Revenue generated by customers who had their first invoice during the period.',
              [RevenueChangeGroup.NET]:
                'Total net increase or decrease in agreement revenue vs the prior period.',
              [RevenueChangeGroup.TOTAL]: '',
            },
          },
        },
      },
      [Dashboard.AGREEMENT_REVENUE_OVERVIEW]: {
        legend: {
          item: {
            children: {
              [RevenueChangeGroup.SAME]: 'Existing Revenue',
              [RevenueChangeGroup.CHURNED]: 'Churned',
              [RevenueChangeGroup.DECREASED]: 'Decreased Revenue',
              [RevenueChangeGroup.EXISTING]: 'Existing Revenue',
              [RevenueChangeGroup.INCREASED]: 'Increased Revenue',
              [RevenueChangeGroup.NEW]: 'New Revenue',
              [RevenueChangeGroup.NET]: 'Net Change',
              [RevenueChangeGroup.TOTAL]: 'Total Revenue',
            },
            tooltipText: {
              [RevenueChangeGroup.SAME]: '',
              [RevenueChangeGroup.CHURNED]: '',
              [RevenueChangeGroup.EXISTING]: `Net revenue that hasn't changed from prior period.`,
              [RevenueChangeGroup.DECREASED]:
                'Net decrease in revenue vs prior period for customers who decreased agreement spend.',
              [RevenueChangeGroup.INCREASED]:
                'Net increase in revenue vs prior period for customers who increased agreement spend.',
              [RevenueChangeGroup.NEW]:
                'Revenue generated by customers who had their first invoice during the period',
              [RevenueChangeGroup.NET]: '',
              [RevenueChangeGroup.TOTAL]:
                'Total agreement revenue during the period.',
            },
          },
        },
      },
    },
  },
  title: {
    home: 'Overview',
    companies: 'Companies',
    portfolio: 'Portfolio',
    contacts: 'Contacts',
    change_password: 'Change Password',
    get_in_touch: 'Get in touch',
  },
  role_type: {
    DECISION_MAKER: 'C-Suite',
    IMPORTANT_DOER: 'Manager',
    SUPPORT: 'Support',
    1: 'Senior Influencer',
    2: 'Decision Maker',
    3: 'Team Member',
    default: 'Other',
  },
  categories,
  health: {
    0: 'Inactive',
    1: 'Fair',
    2: 'Healthy',
    inactive: 'Inactive',
    fair: 'Fair',
    healthy: 'Healthy',
    default: '',
  },
  alarms: [
    { value: '', label: 'None' },
    { value: 0, label: 'Same time' },
    { value: 15, label: '15 minutes before' },
    { value: 60, label: '1 hour before' },
    { value: 60 * 2, label: '2 hours before' },
    { value: 60 * 24, label: '1 day before' },
    { value: 60 * 24 * 2, label: '2 days before' },
    { value: 60 * 24 * 7, label: '1 week before' },
    { value: 60 * 24 * 7 * 4, label: '4 weeks before' },
  ],
  actions: {
    add_note: 'Add a note',
    log_call: 'Log a call',
    log_email: 'Log an email',
    log_meeting: 'Log a meeting',
    save: 'Save',
    savePreferences: 'Save preferences',
    sendInvites: 'Send Invites',
    submitImport: 'Submit Import request',
    submit: 'Submit',
    cancel: 'Cancel',
  },
  duration: [
    { value: 5, label: '5 min' },
    { value: 10, label: '10 min' },
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '1 hour' },
    { value: 90, label: '1hr 30min' },
    { value: 120, label: '2hrs' },
    { value: 180, label: '3hrs' },
  ],
  interaction: {
    viewModalTitle: 'Interaction',
    form: {
      call_with: 'Call with...',
      email: 'Email: ',
      meeting_with: 'Meeting with...',
      write_note: 'Write a note',
      subject: 'Subject',
      title: 'Title',
      attendees: 'Attendees',
    },
    type: {
      call: 'call',
      callPlural: 'calls',
      email: 'email',
      emailPlural: 'emails',
      meeting: 'meeting',
      meetingPlural: 'meetings',
    },
    toast: {
      added_call: 'You have added a new call',
      added_email: 'You have added a new email',
      added_meeting: 'You have added a new meeting',
    },
  },
  validation: {
    [ErrorType.EXISTS]: 'This value already exists in the system.',
    [ErrorType.IMPORT_DUPLICATE]: 'This is a duplicate value.',
    [ErrorType.MANDATORY]: 'This is a required field.',
    required: 'This is a required field.',
    email: 'Please enter a valid email address',
    emailDomain: 'Please enter a valid email domain',
    attendees: 'Must be at least one contact & user in attendance',
    meetingDates: 'The start of the meeting must be prior to the end time',
    onboarding: {
      [TokenValidationFailure.EXISTS]:
        'An account already exists for this user. Try signing in',
      [TokenValidationFailure.INVALID]: 'Please enter a valid email & token',
      [TokenValidationFailure.EXPIRED]: 'This invitation token has expired',
    },
  },
  common: {
    date: 'Date',
    duration: 'duration',
    location: 'Location',
    subject: 'Subject',
    time: 'Time',
    to: 'to',
  },
  portfolio: {
    title: 'Portfolio',
    description: 'Portfolio is account defined company segmentation',
  },
  category: {
    title: 'Tier',
    importance: 'Tier',
    description:
      'A tier is a grouping of companies by importance. Platinum is the most important tier, bronze least.',
  },
  rating: {
    title: 'Rating',
    description: 'Team rating of interaction',
  },
  role: {
    title: 'Role',
    type: 'Role Type',
    description:
      'Role type represents the influence a contact has within their company',
  },
  invoiceStatus: {
    paid: 'Paid',
    draft: 'Draft',
    open: 'Open',
    uncollectible: 'Uncollectible',
    void: 'Void',
  },
};

export default language;
